import React from "react";
import {
  Building2,
  Store,
  CalendarPlus,
  AlarmClock,
} from "lucide-react";

const KEYS = [
  {
    key: "affiliated",
    label: "Trực thuộc",
    icon: Building2,
    iconClass: "text-sky-600",
    activeBg: "bg-sky-50",
  },
  {
    key: "franchise",
    label: "Nhượng quyền",
    icon: Store,
    iconClass: "text-amber-600",
    activeBg: "bg-amber-50",
  },
  {
    key: "franchise_new_year",
    label: "NQ mới trong năm",
    icon: CalendarPlus,
    iconClass: "text-emerald-600",
    activeBg: "bg-emerald-50",
  },
  {
    key: "franchise_expiring_year",
    label: "NQ sắp hết hạn (năm nay)",
    icon: AlarmClock,
    iconClass: "text-rose-600",
    activeBg: "bg-rose-50",
  },
];

function countForKey(stats, key) {
  if (!stats) return 0;
  switch (key) {
    case "affiliated":
      return Number(stats.affiliated_count ?? 0);
    case "franchise":
      return Number(stats.franchise_count ?? 0);
    case "franchise_new_year":
      return Number(stats.franchise_new_this_year ?? 0);
    case "franchise_expiring_year":
      return Number(stats.franchise_expiring_this_year ?? 0);
    default:
      return 0;
  }
}

/**
 * @param {object} stats — từ GET /hrm/branches/stats/summary
 * @param {string} activeKey — filters.dashboard
 * @param {(key: string) => void} onSelect — bấm lại cùng key để bỏ lọc (chuỗi rỗng)
 */
export default function BranchMiniDashboard({ stats, activeKey, onSelect }) {
  return (
    <>
      {/* Mobile: pill một hàng — chỉ icon + số */}
      <div className="mb-4 md:hidden" aria-label="Lọc nhanh theo loại chi nhánh">
        <div className="flex items-center justify-between gap-1 overflow-hidden rounded-full border border-gray-100 bg-white p-1 shadow-sm">
          {KEYS.map((item) => {
            const Icon = item.icon;
            const n = countForKey(stats, item.key);
            const active = activeKey === item.key;
            return (
              <button
                key={item.key}
                type="button"
                title={item.label}
                aria-pressed={active}
                aria-label={`${item.label}: ${n}`}
                onClick={() =>
                  onSelect(active ? "" : item.key)
                }
                className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full px-1 py-2.5 transition-all sm:gap-2 sm:px-2 ${
                  active ? `${item.activeBg} shadow-inner` : "hover:bg-gray-50 active:scale-[0.98]"
                }`}
              >
                <Icon
                  className={`h-[18px] w-[18px] shrink-0 sm:h-5 sm:w-5 ${
                    active ? item.iconClass : "text-gray-400"
                  }`}
                  strokeWidth={2.25}
                />
                <span
                  className={`text-base font-bold leading-none tabular-nums ${
                    active ? "text-gray-800" : "text-gray-600"
                  }`}
                >
                  {n}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop: có nhãn */}
      <div className="mb-6 hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
        {KEYS.map((item) => {
          const Icon = item.icon;
          const n = countForKey(stats, item.key);
          const active = activeKey === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => onSelect(active ? "" : item.key)}
              className={`rounded-2xl border p-4 text-left shadow-sm transition-all ${
                active
                  ? `border-red-200 ${item.activeBg} ring-2 ring-red-100`
                  : "border-gray-100 bg-white hover:border-red-100 hover:shadow-md"
              }`}
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                  {item.label}
                </p>
                <div
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    active ? "bg-white/80" : "bg-gray-50"
                  }`}
                >
                  <Icon className={`h-5 w-5 ${item.iconClass}`} strokeWidth={2.25} />
                </div>
              </div>
              <p className="text-3xl font-black leading-none text-gray-800 tabular-nums">{n}</p>
            </button>
          );
        })}
      </div>
    </>
  );
}
